/**
 * Hook para importação de contatos via arquivo CSV/Excel
 * Responsabilidade: Gerenciar lógica de importação e validação
 * Suporta: CSV, XLS, XLSX
 */

import { useState, useCallback } from 'react';
import { readSheet } from 'read-excel-file/browser';
import { createModuleLogger } from '../utils/logger';

const log = createModuleLogger('[useImportContacts]');

export interface ImportedContact {
    name: string;
    whatsapp?: string;
    phone?: string;
    email?: string;
    // Campos brutos para referência
    [key: string]: any;
}

export interface UseImportContactsReturn {
    isParsing: boolean;
    parseFile: (file: File) => Promise<{ data: Record<string, any>[], headers: string[] }>;
    processContacts: (data: Record<string, any>[], columnMap: Record<string, string>) => ImportedContact[];
}

/**
 * Normaliza nomes de colunas para sugestão inicial de mapeamento
 */
export const suggestMapping = (headers: string[]): Record<string, string> => {
    const mapping: Record<string, string> = {};

    headers.forEach(header => {
        const h = header.toLowerCase().trim();
        
        // Mapeamento de nome
        if (h.includes('nome') || h === 'name' || h === 'cliente' || h.includes('full name')) {
            mapping[header] = 'name';
        }
        // Mapeamento de WhatsApp
        else if (h.includes('whatsapp') || h.includes('celular') || h.includes('mobile') || h === 'cel' || h === 'whats') {
            mapping[header] = 'whatsapp';
        }
        // Mapeamento de telefone - apenas se não achou whatsapp ou se for específico
        else if (h.includes('telefone') || h.includes('phone') || h === 'tel' || h === 'fone') {
            // Se já tem whatsapp mapeado, talvez este seja telefone fixo, mas nosso modelo alvo prioriza um
            if (!Object.values(mapping).includes('whatsapp')) {
                mapping[header] = 'whatsapp'; // Usamos whatsapp como campo principal de contato
            } else {
                mapping[header] = 'phone';
            }
        }
        // Mapeamento de email
        else if (h.includes('email') || h.includes('e-mail') || h === 'mail') {
            mapping[header] = 'email';
        }
    });

    return mapping;
};

/**
 * Limpa e formata número de telefone/WhatsApp
 */
const cleanPhoneNumber = (value: string | number | undefined): string => {
    if (!value) return '';
    // Remove caracteres não numéricos exceto +
    return String(value).replace(/[^\d+]/g, '');
};

/**
 * Sanitiza campos de texto para prevenir CSV Injection
 */
const sanitizeText = (value: string): string => {
    if (!value) return '';
    return String(value).replace(/^[=+\-@\t\r]/, '').trim();
};

export function useImportContacts(): UseImportContactsReturn {
    const [isParsing, setIsParsing] = useState(false);

    /**
     * Importa contatos de arquivo CSV, XLS ou XLSX e retorna dados brutos
     */
    const parseFile = useCallback(async (file: File): Promise<{ data: Record<string, any>[], headers: string[] }> => {
        setIsParsing(true);

        try {
            const fileName = file.name.toLowerCase();
            const isExcel = fileName.endsWith('.xls') || fileName.endsWith('.xlsx');
            const isCsv = fileName.endsWith('.csv') || fileName.endsWith('.txt');

            if (!isExcel && !isCsv) {
                throw new Error('Formato de arquivo não suportado. Use CSV, XLS ou XLSX.');
            }

            log.info('Iniciando parse de arquivo:', { fileName, type: file.type, size: file.size });

            let data: Record<string, any>[];
            let headers: string[] = [];

            if (isExcel) {
                if (fileName.endsWith('.xls')) {
                    throw new Error(
                        'Formato .xls legado não é suportado. Salve o arquivo como .xlsx e tente novamente.'
                    );
                }

                const rows = await readSheet(file);
                if (!rows.length) throw new Error('Arquivo Excel vazio ou inválido');

                headers = rows[0].map((cell) => String(cell ?? '').trim());
                data = rows.slice(1).map((row) => {
                    const record: Record<string, any> = {};
                    headers.forEach((header, index) => {
                        const value = row[index];
                        record[header] = value === null || value === undefined ? '' : value;
                    });
                    return record;
                });
            } else {
                const text = await file.text();
                const lines = text.split(/\r?\n/).filter(line => line.trim());

                if (lines.length < 2) throw new Error('Arquivo vazio ou inválido');

                const firstLine = lines[0];
                const delimiter = firstLine.includes(';') ? ';' : firstLine.includes('\t') ? '\t' : ',';
                
                headers = lines[0].split(delimiter).map(h => h.trim().replace(/^["']|["']$/g, ''));

                data = [];
                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
                    const row: Record<string, any> = {};
                    headers.forEach((header, index) => {
                        row[header] = values[index] || '';
                    });
                    data.push(row);
                }
            }

            if (data.length === 0) throw new Error('Nenhum dado encontrado no arquivo');

            return { data, headers };

        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao ler arquivo';
            log.error('Falha no parse:', error);
            throw new Error(message);
        } finally {
            setIsParsing(false);
        }
    }, []);

    /**
     * Processa os dados brutos aplicando o mapeamento de colunas
     */
    const processContacts = useCallback((data: Record<string, any>[], columnMap: Record<string, string>): ImportedContact[] => {
        const processedContacts: ImportedContact[] = [];

        // Inverter mapa para facilitar busca por campo alvo
        // Ex: { 'Nome Completo': 'name' } -> busca na linha['Nome Completo'] para preencher contact.name
        
        for (const row of data) {
            const contact: ImportedContact = {
                name: '',
                whatsapp: '',
                email: '',
                phone: ''
            };

            let hasData = false;

            Object.entries(columnMap).forEach(([sourceColumn, targetField]) => {
                if (!targetField || targetField === 'ignore') return;

                const value = row[sourceColumn];
                if (value) {
                    const stringValue = String(value);
                    
                    if (targetField === 'name') {
                        contact.name = sanitizeText(stringValue);
                    } else if (targetField === 'whatsapp') {
                        contact.whatsapp = cleanPhoneNumber(stringValue);
                    } else if (targetField === 'email') {
                        contact.email = sanitizeText(stringValue).toLowerCase();
                    } else if (targetField === 'phone') {
                        contact.phone = cleanPhoneNumber(stringValue);
                    }
                }
            });

            // Fallback: se whatsapp vazio mas tem phone, usa phone
            if (!contact.whatsapp && contact.phone) {
                contact.whatsapp = contact.phone;
            }

            // Validação mínima
            if (contact.name && contact.name.length >= 2) {
                processedContacts.push(contact);
            }
        }

        return processedContacts;
    }, []);

    return {
        isParsing,
        parseFile,
        processContacts
    };
}
